// SafeCurves assistant store behind the chat panel: 'data' mode drives the map via `/query/agent/stream`, 'docs' mode is RAG over the playbook PDFs.
// Each mode keeps its own transcript and server `chat_id`; the AI `session_id` from the availability handshake is shared.

import { getLogger } from '@logtape/logtape';
import * as aiApi from './aiApiClient';
import { AiApiError } from './aiApiClient';
import { backoffDelayMs, type BackoffOptions } from './retryBackoff';
import { aiChatApiUrl, aiMapChatEnabled } from '#lib/config.js';
import { geodataStore } from '#lib/stores/geodataStore.svelte.js';
import {
	applyUiActions,
	clearAgentHighlights,
	resetFeaturePoints,
	collectUiState,
	enterAiDisplayMode,
	exitAiDisplayMode
} from './uiActions';

const log = getLogger(['safesystem', 'safecurves', 'chat-store']);

export type ChatMode = 'data' | 'docs';

export const CHAT_MODES: { id: ChatMode; label: string }[] = [
	{ id: 'data', label: 'Map & Data' },
	{ id: 'docs', label: 'Project Documents' }
];

type DataChatRole = 'human' | 'ai' | 'status' | 'error';

interface IDataChatEntry {
	id: string;
	role: DataChatRole;
	text: string;
}

interface IConversation {
	entries: IDataChatEntry[];
	chatId: string | null;
}

// Each agent step replaces one live status line: users found a running list of tool calls noisy.
const TOOL_PHRASES: Record<string, string> = {
	list_data_layers: 'checking what data is available…',
	describe_layer: 'reading the data structure…',
	list_scope_values: 'listing the available areas…',
	count_features: 'counting matching features…',
	query_features: 'fetching matching records…',
	find_features_near: 'searching for features near each other…',
	show_only_features: 'updating the map…',
	hide_layers: 'updating the map…',
	clear_data_filter: 'clearing the map…',
	highlight_features: 'highlighting results…',
	clear_highlights: 'clearing highlights…',
	zoom_map: 'moving the map…',
	switch_view: 'switching view…',
	switch_module: 'switching module…'
};

/** The agent API degrades badly on very long prompts, so cap what we send. */
export const MAX_PROMPT_LENGTH = 500;

// Generous enough for a multi-tool agent turn, short enough that a hung request
// releases the composer instead of blocking it forever.
const REQUEST_TIMEOUT_MS = 180_000;

// Availability check auto-recovers from transient (non-403) failures over a
// bounded, jittered backoff (see retryBackoff.ts).
const AVAILABILITY_MAX_ATTEMPTS = 10;
const AVAILABILITY_BACKOFF: BackoffOptions = { baseMs: 1_000, minMs: 1_000, capMs: 120_000 };

/** Resolve after `ms`, or immediately when `signal` aborts (caller re-checks). */
function sleep(ms: number, signal: AbortSignal): Promise<void> {
	return new Promise((resolve) => {
		if (signal.aborted) return resolve();
		const id = setTimeout(resolve, ms);
		signal.addEventListener(
			'abort',
			() => {
				clearTimeout(id);
				resolve();
			},
			{ once: true }
		);
	});
}

function emptyConversation(): IConversation {
	return { entries: [], chatId: null };
}

function createChatStore() {
	let isOpen = $state(false);
	// Default to docs when the Map & Data agent is disabled (that mode isn't offered).
	let mode = $state<ChatMode>(aiMapChatEnabled ? 'data' : 'docs');
	const conversations = $state<Record<ChatMode, IConversation>>({
		data: emptyConversation(),
		docs: emptyConversation()
	});
	let currentPrompt = $state('');
	let sessionId = $state<string | null>(null);
	// Which mode has a request in flight (null = idle). One request at a time,
	// so the spinner can be shown on the tab that owns it.
	let loadingMode = $state<ChatMode | null>(null);
	let liveStatus = $state<string | null>(null);
	// The opening screen asks which mode to work in; it stays until one is
	// picked, and returns on New chat because that is a fresh start.
	let modeChosen = $state(false);
	// Whether this key is entitled to the assistant. 'unknown' is retryable;
	// 'unavailable' means the service refused the key, which is a legitimate
	// state for a customer who has not bought it.
	let availability = $state<'unknown' | 'checking' | 'available' | 'unavailable'>('unknown');
	// The key `availability` describes, so a key change restarts the check, and
	// the controller that cancels an in-flight recovery loop when a newer check
	// supersedes it.
	let availabilityKey: string | null = null;
	let availabilityRetry: AbortController | null = null;
	// Cancels whatever is in flight when the user stops it, closes the panel, or
	// the timeout fires.
	let inFlight: AbortController | null = null;

	const isLoading = $derived(loadingMode !== null);
	const entries = $derived(conversations[mode].entries);
	const canSubmit = $derived(
		currentPrompt.trim().length > 0 &&
			currentPrompt.length <= MAX_PROMPT_LENGTH &&
			loadingMode === null
	);

	/** Writes the display-mode banner once per data conversation. */
	function noteAiDisplayMode() {
		if (conversations.data.entries.length > 0) return;
		append(
			'data',
			'status',
			'➤ AI display mode — I control the map now; ask me to adjust what you see'
		);
	}

	function append(target: ChatMode, role: DataChatRole, text: string) {
		conversations[target].entries = [
			...conversations[target].entries,
			{ id: crypto.randomUUID(), role, text }
		];
	}

	/** Map & Data: streaming agent call whose actions drive the map. */
	async function sendDataPrompt(prompt: string, apiKey: string, signal: AbortSignal) {
		const context = {
			apiKey,
			sessionId,
			chatId: conversations.data.chatId,
			uiState: collectUiState(),
			signal
		};

		// Streaming path: each step replaces the live status line so the user
		// sees that work is happening, without a running log of tool calls.
		let result: aiApi.AgentQueryResponse;
		try {
			result = await aiApi.queryAgentStream(prompt, context, (event) => {
				if (event.type === 'status') {
					liveStatus = event.message;
				} else if (event.type === 'tool') {
					liveStatus = TOOL_PHRASES[event.tool] ?? 'working on it…';
				}
			});
		} catch (streamError) {
			// Retry without streaming ONLY when the endpoint isn't there to talk to.
			// A mid-stream failure means the agent may already have run its WFS
			// queries, and re-running them is expensive and duplicated.
			if (!(streamError instanceof aiApi.AgentStreamUnsupportedError)) throw streamError;
			log.warn('agent stream unsupported, falling back {error}', { error: streamError });
			result = await aiApi.queryAgent(prompt, context);
		}

		// A genuine cancel (stop button / timeout) discards the turn; a mere close does not.
		if (signal.aborted) return;

		conversations.data.chatId = result.chat_id ?? conversations.data.chatId;
		// Only drive the map while the panel is open: a closed panel means the user
		// went back to their manual view, so keep the reply but apply no map actions.
		if (isOpen) {
			for (const line of applyUiActions(result.ui_actions ?? [])) {
				append('data', 'status', `➤ ${line}`);
			}
		}
		append('data', 'ai', result.response);
	}

	/** Project Documents: RAG over the playbook / methodology PDFs. */
	async function sendDocsPrompt(prompt: string, apiKey: string, signal: AbortSignal) {
		const result = await aiApi.query(prompt, {
			apiKey,
			sessionId,
			chatId: conversations.docs.chatId,
			signal
		});
		if (signal.aborted) return;
		conversations.docs.chatId = result.chat_id ?? conversations.docs.chatId;
		append('docs', 'ai', result.response);
	}

	return {
		get isOpen() {
			return isOpen;
		},
		get mode() {
			return mode;
		},
		get showModeChooser() {
			// No chooser when only doc chat exists — open straight into it.
			return !modeChosen && aiMapChatEnabled;
		},
		// Enabled for this key (configData flag) with a chat host configured; `isAvailable` is the AI service's own check.
		get enabled() {
			return geodataStore.configData?.safecurves_ai === true && Boolean(aiChatApiUrl);
		},
		get isAvailable() {
			return availability === 'available';
		},

		/**
		 * Verify the key before offering the assistant. 403 is terminal (not
		 * entitled); a transient failure auto-recovers over a bounded backoff. The
		 * handshake's session is reused.
		 */
		async ensureAvailable(apiKey: string) {
			// Skip if this key is settled or checking; a new key or retryable 'unknown' restarts.
			if (availabilityKey === apiKey && availability !== 'unknown') return;

			availabilityRetry?.abort();
			const controller = new AbortController();
			availabilityRetry = controller;
			availabilityKey = apiKey;
			availability = 'checking';

			let lastError: unknown;
			for (let attempt = 0; attempt < AVAILABILITY_MAX_ATTEMPTS; attempt++) {
				try {
					const { session_id } = await aiApi.validateApiKey(apiKey, controller.signal);
					if (controller.signal.aborted) return;
					sessionId = session_id;
					availability = 'available';
					return;
				} catch (error) {
					if (controller.signal.aborted) return; // superseded by a newer check
					if (error instanceof AiApiError && error.status === 403) {
						// 403: the service refused this key; stay hidden.
						availability = 'unavailable';
						log.info('assistant is not enabled for this key');
						return;
					}
					// Transient: remember it, retry on backoff, log once at the end.
					lastError = error;
					if (attempt === AVAILABILITY_MAX_ATTEMPTS - 1) break;
					await sleep(backoffDelayMs(attempt, AVAILABILITY_BACKOFF), controller.signal);
					if (controller.signal.aborted) return;
				}
			}
			// All attempts failed: log once, stay retryable for the next trigger.
			availability = 'unknown';
			if (availabilityRetry === controller) availabilityRetry = null;
			log.warn('assistant availability check failed after {attempts} attempts {error}', {
				attempts: AVAILABILITY_MAX_ATTEMPTS,
				error: lastError
			});
		},
		get entries() {
			return entries;
		},
		get currentPrompt() {
			return currentPrompt;
		},
		get isLoading() {
			return isLoading;
		},
		/** Mode that owns the in-flight request, so only its tab shows a spinner. */
		get loadingMode() {
			return loadingMode;
		},
		get liveStatus() {
			return liveStatus;
		},
		get canSubmit() {
			return canSubmit;
		},

		/**
		 * Opening the panel enters AI display mode: the agent controls the map,
		 * starting from the view the user had. Closing exits and restores their
		 * manual view untouched. Deliberately tied to the panel, not the tab —
		 * a data result stays on screen while you read the documents about it.
		 */
		open() {
			isOpen = true;
			enterAiDisplayMode();
			// The banner is written when the data transcript is entered, not here:
			// on first open the chooser is showing and no mode has been picked yet.
			if (modeChosen) noteAiDisplayMode();
		},

		/** Opening screen: pick a mode, optionally seeding the composer. */
		chooseMode(next: ChatMode, prompt?: string) {
			mode = next;
			modeChosen = true;
			if (next === 'data') noteAiDisplayMode();
			if (prompt) currentPrompt = prompt;
		},

		close() {
			// Leave any in-flight turn running so its reply still lands in the
			// transcript; the reply applies no map actions while closed.
			isOpen = false;
			exitAiDisplayMode();
		},

		toggle() {
			if (isOpen) {
				this.close();
			} else {
				this.open();
			}
		},

		setMode(next: ChatMode) {
			mode = next;
			if (next === 'data') noteAiDisplayMode();
		},

		setPrompt(text: string) {
			currentPrompt = text;
		},

		/** Send the staged prompt through the active mode's backend. */
		async sendPrompt(apiKey: string) {
			const prompt = currentPrompt.trim();
			if (!prompt || loadingMode !== null || prompt.length > MAX_PROMPT_LENGTH) return;

			const target = mode;
			currentPrompt = '';
			append(target, 'human', prompt);
			loadingMode = target;

			// `controller` is the user's stop button; the timeout aborts with a `TimeoutError`.
			const controller = new AbortController();
			inFlight = controller;
			const signal = AbortSignal.any([controller.signal, AbortSignal.timeout(REQUEST_TIMEOUT_MS)]);

			try {
				if (target === 'data') {
					await sendDataPrompt(prompt, apiKey, signal);
				} else {
					await sendDocsPrompt(prompt, apiKey, signal);
				}
			} catch (error) {
				// Stopping is a user action, so only a timeout is worth reporting back.
				if (signal.aborted) {
					const timedOut = (signal.reason as Error | undefined)?.name === 'TimeoutError';
					log.warn('assistant request aborted, timedOut={timedOut}', { timedOut });
					if (timedOut) {
						append(
							target,
							'error',
							'That took longer than expected, so I stopped waiting. Try asking for a smaller area or fewer records.'
						);
					}
					return;
				}
				// The status line and code are for the log, not the transcript.
				log.error('assistant request failed {error}', { error });
				append(
					target,
					'error',
					'Something went wrong on the assistant side. Please try that again.'
				);
			} finally {
				if (inFlight === controller) inFlight = null;
				loadingMode = null;
				liveStatus = null;
			}
		},

		/** Stop an in-flight request (the composer's stop button). */
		cancel() {
			inFlight?.abort();
		},

		/** End the current mode's server-side chat and reset just that transcript. */
		async startNewChat(apiKey: string) {
			const target = mode;
			const current = conversations[target].chatId;
			conversations[target] = emptyConversation();
			modeChosen = false;
			if (target === 'data') {
				clearAgentHighlights();
				resetFeaturePoints();
			}
			if (!current || !sessionId) return;
			try {
				await aiApi.endChat({ apiKey, sessionId, chatId: current });
			} catch (error) {
				// Non-fatal — the new chat starts regardless.
				log.warn('endChat failed during startNewChat {error}', { error });
			}
		}
	};
}

export const dataChatStore = createChatStore();
